import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isValidJson } from "../core/utils/validators.ts";
import { Arquivo } from "../models/ArquivoModel.ts";
import { Aviso } from "../models/AvisoModel.ts";
import { Coluna } from "../models/ColunaModel.ts";
import { Cor } from "../models/CorModel.ts";
import { Filial } from "../models/Filial.ts";
import { Notificacao } from "../models/NotificacaoModel.ts";
import Profile from "../models/Profile.model.ts";
import { Usuario } from "../models/Usuario.model.ts";
import { getEmpresaPorId } from "../services/ModuloGerenciador/Administrativo.js";
import {
    getNotificacoes,
    getRegras,
} from "../services/ModuloGerenciador/Geral.ts";
import { getHome } from "../services/ModuloGerenciador/Home.js";
import { getMidiaIdUsuario } from "../services/ModuloGerenciador/Midia.js";
import { logoutUser } from "../services/ModuloGerenciador/Usuario.js";
import { getUsuarioTabelaPorId } from "../services/ModuloGerenciador/UsuarioTabela.js";
import { colunasUsuarioInicial } from "./ColunasUsuarioInicial.ts";
import {
    colunasEmpresaInicial,
    initialColor,
} from "./ContextConstants.ts";
import ContextoEmbarque from "./ContextoEmbarque.js";
import { useCor } from "./Cor.tsx";
import { useEmbarqueDependencias } from "./EmbarqueDependenciasContext.tsx";
import { useCidade } from "./FilialContext.tsx";
import { useMenus } from "./Menus.tsx";
import { useNomeTabela } from "./NomeTabela.tsx";
import DadosUsuarioRedux from "./Usuario";

export interface ColunasInicial {
    [key: string]: Coluna[];
}

interface DadosIniciais {
    avisos: Aviso[];
    notificacoes: Notificacao[];
    regras: string[];
    profileImage?: Arquivo;
    usuarioTabela: ColunasInicial;
}

interface HandleLoginProps {
    id: number;
    token: string;
    tabelas: string;
    tenant: string;
    usuario: Usuario;
}

interface ProfileContextType {
    logout: () => void;
    profile?: Profile;
    tabelaEmpresa: ColunasInicial;
    tabelaUsuario: ColunasInicial;
    coresEmpresa: Cor;
    dadosFilial?: Filial[];
    contextoEmbarque?: string;
    handleLoadData: () => Promise<void>;
    handleSetContextoEmbarque: (contexto: string) => void;
    setContextoEmbarque: (contexto: string) => void;
    qtPedidosAguardando: number;
    numeroCte: number;
    cteDivergentes?: number;
    setCoresEmpresa: (cores: Cor) => void;
    setTabelaEmpresa: (tabela: ColunasInicial) => void;
    setTabelaUsuario: (tabela: ColunasInicial) => void;
    isLoading: boolean;
    hasRegra: (regra: string) => boolean;
    handleLogin: (props: HandleLoginProps) => Promise<void>;
    buscarNumeroSolicitacoes: () => void;
    shouldSearchDependencies: () => void;
    contextoEmbarqueMudou: boolean;
    setContextoEmbarqueMudou: (valor: boolean) => void;
}

interface ProfileProviderProps {
    children: ReactNode;
}

const getColunaKey = (coluna: Coluna) =>
    coluna.field ?? coluna.campo ?? coluna.nome ?? "";

function mergeColunaArrays<T extends Coluna>(
    existing: T[],
    incoming: T[]
): T[] {
    const map = new Map<string, T>();

    existing.forEach((col) => {
        const key = getColunaKey(col);
        if (!key) return;
        map.set(key, col);
    });

    incoming.forEach((colNova) => {
        const key = getColunaKey(colNova);
        if (!key) return;

        const colAntiga = map.get(key);
        if (colAntiga) {
            map.set(key, { ...colAntiga, ...colNova });
        } else {
            map.set(key, colNova);
        }
    });

    const result: T[] = [];

    existing.forEach((col) => {
        const key = getColunaKey(col);
        if (!key) return;
        const merged = map.get(key) ?? col;
        result.push(merged);
        map.delete(key);
    });

    map.forEach((col) => {
        result.push(col);
    });

    return result;
}

export const ProfileContext = createContext<ProfileContextType>({
    logout: async () => {
    },
    handleLoadData: async () => {
    },
    handleSetContextoEmbarque: async () => {
    },
    setContextoEmbarque: async () => {
    },
    tabelaEmpresa: colunasEmpresaInicial,
    tabelaUsuario: colunasUsuarioInicial,
    coresEmpresa: initialColor,
    setCoresEmpresa: async () => {
    },
    setTabelaEmpresa: async () => {
    },
    setTabelaUsuario: async () => {
    },
    isLoading: false,
    hasRegra: () => false,
    qtPedidosAguardando: 0,
    numeroCte: 0,
    handleLogin: async () => {
    },
    buscarNumeroSolicitacoes: async () => {
    },
    shouldSearchDependencies: async () => {
    },
    contextoEmbarqueMudou: false,
    setContextoEmbarqueMudou: async () => {
    },
});

export const ProfileProvider = ({ children }: ProfileProviderProps) => {
    const [profile, setProfile] = useState<Profile>();
    const [dadosFilial, setDadosFilial] = useState<Filial[]>([]);
    const [tabelaEmpresa, setTabelaEmpresa] = useState<ColunasInicial>(
        colunasEmpresaInicial
    );
    const [coresEmpresa, setCoresEmpresa] = useState<Cor>(initialColor);
    const [tabelaUsuario, setTabelaUsuario] = useState<ColunasInicial>(
        colunasUsuarioInicial
    );
    const [contextoEmbarque, setContextoEmbarque] = useState<string>("");
    const [contextoEmbarqueMudou, setContextoEmbarqueMudou] = useState<boolean>(false);
    const [isLoading, setisLoading] = useState(true);
    const [qtPedidosAguardando, setQuantidadePedidosAguardando] =
        useState<number>(0);
    const [numeroCte, setNumeroCte] = useState<number>(0);
    const [cteDivergentes, setCteDivergentes] = useState<number>(0);
    const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
    const [shouldSearch, setShouldSearch] = useState<boolean>(true);

    const contextoAnterior = useRef<string>("");

    const { setDadosMenusRedux } = useMenus();
    const { setDadosTabelaRedux } = useNomeTabela();
    const { setDadosCoresRedux } = useCor();
    const { setDadosFilialRedux } = useCidade();
    const { setDadosUsuarioRedux } = useContext(DadosUsuarioRedux);
    const { setDadosContextoEmbarqueRedux } = useContext(ContextoEmbarque);
    const { fetchEmbarqueEDependencias, buscandoEmbarqueEDependencias } =
        useEmbarqueDependencias();

    const navigate = useNavigate();
    const location = useLocation();

    const isRotaViagens = useMemo(
        () => location.pathname.includes("/viagens/"),
        [location.pathname]
    );

    const isCadastro = useMemo(
        () => location.pathname.includes("/cadastro"),
        [location.pathname]
    );

    const contextoPorUrl = useMemo(() => {
        return location.pathname.split("/")[2] || "todos";
    }, [location.pathname]);

    const updateLastFetchTime = useCallback(() => {
        setLastFetchTime(Date.now());
    }, []);

    const shouldSearchDependencies = useCallback(() => {
        setShouldSearch(true);
    }, []);

    const buscarRegras = useCallback(async ({ token, id }: { token: string; id: number }): Promise<string[]> => {
        try {
            const result = await getRegras(token, id);
            if (result.status === 200) {
                return result.data.map((dado: any) => dado.regras.regra);
            }
        } catch (error) {
            console.error(error);
        }
        return [];
    }, []);

    const buscarColunasUser = useCallback(async ({ token, id }: { token: string; id: number }) => {
        try {
            const result = await getUsuarioTabelaPorId({ token, id });
            return result.data;
        } catch (error) {
            console.error(error);
            return {};
        }
    }, []);

    const handleSetContextoEmbarque = useCallback(
        (contexto: string) => {
            if (!contexto) return;
            setisLoading(true);
            setContextoEmbarque(contexto);
            setDadosContextoEmbarqueRedux(contexto);
            localStorage.setItem("contextoEmbarque", JSON.stringify(contexto));
            setTimeout(() => {
                setisLoading(false);
            }, 1000);
        },
        [setDadosContextoEmbarqueRedux]
    );

    const clearLocalStorage = useCallback(() => {
        localStorage.removeItem("dadosFilial");
        localStorage.removeItem("token");
        localStorage.removeItem("usuario");
        localStorage.removeItem("expiracao");
        localStorage.removeItem("Cookies");
        localStorage.removeItem("flagPassword");
        localStorage.removeItem("expirateTime");
        localStorage.removeItem("contextoEmbarque");
        localStorage.removeItem("notificacaoId");
    }, []);

    const buscarMidiaUsuario = useCallback(
        async ({ id, token }: { id: number; token: string }) => {
            try {
                const result = await getMidiaIdUsuario({ id, token });
                return result.data;
            } catch (error) {
                console.error(error);
                return undefined;
            }
        },
        []
    );

    const buscarEmpresaId = useCallback(async () => {
        try {
            await getEmpresaPorId({ tenant: localStorage.getItem("tenant") }).then((result) => {
                if (result?.status === 200) {
                    const nomeColuna = result?.data.colunas.replace(/'/g, '"');
                    const colunas = JSON.parse(nomeColuna);
                    const nomeCores = result?.data.cores.replace(/'/g, '"');
                    const cores = JSON.parse(nomeCores);
                    setDadosTabelaRedux(colunas);
                    setDadosCoresRedux(cores);
                    setTabelaEmpresa((current) => {
                        const merged: ColunasInicial = { ...current };

                        Object.keys(colunas).forEach((key) => {
                            const incoming = colunas[key];
                            const existing = current[key] || [];

                            if (Array.isArray(existing) && Array.isArray(incoming)) {
                                merged[key] = mergeColunaArrays(existing, incoming);
                            } else {
                                merged[key] = incoming;
                            }
                        });

                        return merged;
                    });
                    setCoresEmpresa(cores);
                }
            });
        } catch (error) {
            console.error(error);
        }
    }, [
        setDadosTabelaRedux,
        setDadosCoresRedux,
        setTabelaEmpresa,
        setCoresEmpresa,
    ]);

    const buscarDadosIniciais = useCallback(
        async (token: string, id: number): Promise<DadosIniciais | undefined> => {
            const profileImage = await buscarMidiaUsuario({ id, token });

            try {
                await buscarEmpresaId();
                fetchEmbarqueEDependencias(token);
                updateLastFetchTime();
                setShouldSearch(false);
                const result = await getHome({ token, id });
                if (result.status === 200) {
                    if (result.data.filiais) {
                        setDadosFilial(result.data.filiais);
                        setDadosFilialRedux(result.data.filiais);
                    }
                    if (result.data[0]?.notificacaoId) {
                        localStorage.setItem("notificacaoId", result.data[0].notificacaoId);
                    }
                }

                const regras = await buscarRegras({ token, id });
                const colunasUser = await buscarColunasUser({ token, id });

                const usuarioTabela = colunasUser?.campoVisiveis
                    ? JSON.parse(colunasUser.campoVisiveis.replace(/'/g, '"'))
                    : [];

                return {
                    avisos: result.data.avisos || [],
                    notificacoes: result.data.notificacoes || [],
                    regras: regras || [],
                    usuarioTabela,
                    profileImage: profileImage || undefined,
                };
            } catch (error) {
                console.error(error);
                clearLocalStorage();
                navigate("/login");
            }
        },
        [
            setDadosFilialRedux,
            setDadosFilial,
            buscarEmpresaId,
            buscarMidiaUsuario,
            clearLocalStorage,
            buscarRegras,
            buscarColunasUser,
            navigate,
            fetchEmbarqueEDependencias,
            updateLastFetchTime,
        ]
    );

    const parseJwt = useCallback((token: string) => {
        try {
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map(function (c) {
                        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join("")
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            return null;
        }
    }, []);

    const handleLogin = useCallback(
        async ({ id, token, tabelas, tenant, usuario }: HandleLoginProps) => {
            const dadosIniciais = await buscarDadosIniciais(token, id);

            if (dadosIniciais) {
                const { avisos, notificacoes, regras, profileImage } = dadosIniciais;
                const nomeColuna = tabelas.replace(/'/g, '"');
                const usuarioTabelaColunas = JSON.parse(nomeColuna);

                const decodedToken = parseJwt(token);

                const expiracaoTime =
                    decodedToken && decodedToken.exp
                        ? decodedToken.exp * 1000
                        : new Date().getTime() + 60 * 60 * 1000;

                localStorage.setItem("expiracao", expiracaoTime.toString());

                const regrasList = ["Home", ...regras];

                const profileData: Profile = {
                    ...profile,
                    usuario: usuario,
                    token: token,
                    expiracao: expiracaoTime,
                    tenant,
                    profileImage: profileImage,
                    regras: regrasList,
                    avisos,
                    notificacoes,
                };

                setProfile(profileData);
                setDadosUsuarioRedux(profileData);
                setDadosMenusRedux(usuarioTabelaColunas);
                setTabelaUsuario(usuarioTabelaColunas);
                navigate("/");
            }
        },
        [
            buscarDadosIniciais,
            navigate,
            parseJwt,
            setProfile,
            setDadosUsuarioRedux,
            setDadosMenusRedux,
            setTabelaUsuario,
            profile
        ]
    );

    const removeUserAccess = useCallback(() => {
        clearLocalStorage();
        setProfile(undefined);
        navigate("/login");
    }, [clearLocalStorage, navigate]);

    const logout = useCallback(async () => {
        await logoutUser({ id: profile?.usuario.idUsuario, token: profile?.token });
        removeUserAccess();
    }, [profile, removeUserAccess]);

    const verificarFlagPassword = useCallback(() => {
        if (localStorage.getItem("flagPassword")) {
            navigate("/editar-usuario");
        }
    }, [navigate]);

    const handleLoadData = useCallback(async () => {
        const token = localStorage.getItem("token");
        const tenant = localStorage.getItem("tenant");
        const usuario = localStorage.getItem("usuario");
        const expiracaoStr = localStorage.getItem("expiracao");
        const contexto = localStorage.getItem("contextoEmbarque") || "";

        if (!token || !expiracaoStr || !usuario) {
            removeUserAccess();
            setisLoading(false);
            navigate("/login");
            return;
        }

        const expiracao = parseInt(expiracaoStr, 10);
        if (Date.now() > expiracao) {
            removeUserAccess();
            return;
        }
        const usuarioFormatado = JSON.parse(usuario);
        const contextoFormatado = isValidJson(contexto)
            ? JSON.parse(contexto)
            : "todos";

        verificarFlagPassword();

        if (usuarioFormatado.idUsuario) {
            const dadosIniciais = await buscarDadosIniciais(
                token,
                usuarioFormatado.idUsuario
            );

            if (dadosIniciais) {
                const { avisos, notificacoes, regras, profileImage, usuarioTabela } =
                    dadosIniciais;

                setProfile({
                    token,
                    expiracao,
                    usuario: usuarioFormatado,
                    profileImage: profileImage,
                    tenant: tenant || "",
                    avisos,
                    notificacoes,
                    regras,
                });
                setDadosUsuarioRedux({
                    usuario: usuarioFormatado,
                    token,
                    expiracao,
                });
                setTabelaUsuario(usuarioTabela);
                setDadosMenusRedux(usuarioTabela);
                localStorage.setItem("accessToken", token);

                if (contextoFormatado) {
                    setContextoEmbarque(contextoFormatado);
                    setDadosContextoEmbarqueRedux(contextoFormatado);
                    localStorage.setItem(
                        "contextoEmbarque",
                        JSON.stringify(contextoFormatado)
                    );
                } else if (isRotaViagens && !!contextoPorUrl) {
                    setContextoEmbarque(contextoPorUrl);
                    setDadosContextoEmbarqueRedux(contextoPorUrl);
                    localStorage.setItem(
                        "contextoEmbarque",
                        JSON.stringify(contextoPorUrl)
                    );
                } else {
                    setContextoEmbarque("todos");
                    localStorage.setItem("contextoEmbarque", JSON.stringify("todos"));
                    setDadosContextoEmbarqueRedux("todos");
                }
            }
        }
        setisLoading(false);
    }, [
        removeUserAccess,
        verificarFlagPassword,
        buscarDadosIniciais,
        setDadosUsuarioRedux,
        setDadosContextoEmbarqueRedux,
        isRotaViagens,
        contextoPorUrl,
        setDadosMenusRedux,
        navigate,
    ]);

    const hasRegra = useCallback(
        (regra: string): boolean => {
            return profile?.regras?.includes(regra) || false;
        },
        [profile?.regras]
    );

    const verificarExpiracao = useCallback(() => {
        const expiracaoStr = localStorage.getItem("expiracao");
        if (expiracaoStr) {
            const expiracao = parseInt(expiracaoStr, 10);
            if (Date.now() > expiracao) {
                removeUserAccess();
            }
        }
    }, [removeUserAccess]);

    const buscarNumeroSolicitacoes = useCallback(async () => {
        if (profile?.token) {
            try {
                const response = await getNotificacoes({ token: profile?.token });
                if (response.status === 200) {
                    setQuantidadePedidosAguardando(response.data.nPedidos);
                    setNumeroCte(response.data.nEmbarques);
                    setCteDivergentes(response.data.cteDivergentes);
                }
            } catch (error) {
                console.error("Failed to fetch notifications:", error);
            }
        }
    }, [profile?.token]);

    useEffect(() => {
        buscarNumeroSolicitacoes();
        verificarExpiracao();

        const notificacoesInterval = setInterval(buscarNumeroSolicitacoes, 30000);
        const expiracaoInterval = setInterval(verificarExpiracao, 60000);

        return () => {
            clearInterval(notificacoesInterval);
            clearInterval(expiracaoInterval);
        };
    }, [buscarNumeroSolicitacoes, verificarExpiracao]);

    useEffect(() => {
        if (profile?.token) {
            if (
                isRotaViagens &&
                !buscandoEmbarqueEDependencias &&
                !isCadastro &&
                (!lastFetchTime || Date.now() - parseInt(lastFetchTime?.toString() ?? "", 10) > 3 * 60 * 1000 || shouldSearch)
            ) {
                fetchEmbarqueEDependencias(profile?.token);
                updateLastFetchTime();
                setShouldSearch(false);
            }
        }
    }, [
        profile?.token,
        isRotaViagens,
        contextoEmbarque,
        buscandoEmbarqueEDependencias,
        fetchEmbarqueEDependencias,
        isCadastro,
        lastFetchTime,
        updateLastFetchTime,
        shouldSearch,
    ]);

    useEffect(() => {
        if (contextoAnterior.current && contextoAnterior.current !== contextoEmbarque) {
            setContextoEmbarqueMudou(true);
        } else {
            setContextoEmbarqueMudou(false);
        }
        contextoAnterior.current = contextoEmbarque;
    }, [contextoEmbarque]);

    const contextValue = useMemo(
        () => ({
            logout,
            coresEmpresa,
            dadosFilial,
            tabelaEmpresa,
            profile,
            setProfile,
            handleLoadData,
            contextoEmbarque,
            setContextoEmbarque: handleSetContextoEmbarque,
            handleSetContextoEmbarque,
            isLoading,
            hasRegra,
            setCoresEmpresa,
            setTabelaEmpresa,
            tabelaUsuario,
            setTabelaUsuario,
            handleLogin,
            qtPedidosAguardando,
            numeroCte,
            cteDivergentes,
            buscarNumeroSolicitacoes,
            shouldSearchDependencies,
            contextoEmbarqueMudou,
            setContextoEmbarqueMudou,
        }),
        [
            logout,
            profile,
            handleLoadData,
            coresEmpresa,
            dadosFilial,
            setProfile,
            tabelaEmpresa,
            contextoEmbarque,
            handleSetContextoEmbarque,
            isLoading,
            hasRegra,
            setCoresEmpresa,
            setTabelaEmpresa,
            tabelaUsuario,
            setTabelaUsuario,
            handleLogin,
            qtPedidosAguardando,
            numeroCte,
            cteDivergentes,
            buscarNumeroSolicitacoes,
            shouldSearchDependencies,
            contextoEmbarqueMudou,
            setContextoEmbarqueMudou,
        ]
    );

    return (
        <ProfileContext.Provider value={contextValue}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error("useProfile must be used within a ProfileProvider");
    }
    return context;
};
